// The right-hand panel: Participants (invite, waiting room, raised hands, everyone's mic/camera) or Chat.
import { useState } from 'react';
import { Check, Copy, Crown, Hand, Link2, Mic, MicOff, UserPlus, Video, VideoOff, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import ChatPanel from './ChatPanel';
import { isMember } from './useHostHandoff';

const smBtn = 'inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors duration-150';

function Invite({ share }) {
  const [copied, setCopied] = useState('');
  const copy = async (url, which) => { if (await share.copy(url)) { setCopied(which); setTimeout(() => setCopied(''), 1500); } };
  return (
    <div className="space-y-2.5 border-b border-white/10 p-3" data-testid="mm-share-bar">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[13px] font-medium text-white"><Link2 className="h-4 w-4 text-[#7FA8D6]" /> Invite people</span>
        <button type="button" className={`${smBtn} bg-white/10 text-white hover:bg-white/20`} onClick={() => copy(share.teamUrl, 'team')} data-testid="mm-copy-team-link">
          {copied === 'team' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy team link
        </button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-200">
          <UserPlus className="h-4 w-4 text-[#7FA8D6]" /> Allow guests without a login
          <Switch checked={share.guestEnabled} disabled={share.busy} onCheckedChange={share.toggleGuest} data-testid="mm-guest-toggle" />
        </label>
        {share.guestEnabled && share.guestUrl && (
          <button type="button" className={`${smBtn} bg-white/10 text-white hover:bg-white/20`} onClick={() => copy(share.guestUrl, 'guest')} data-testid="mm-copy-guest-link">
            {copied === 'guest' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy guest link
          </button>
        )}
      </div>
      {share.guestEnabled && <p className="text-[11px] text-slate-400" data-testid="mm-guest-expiry">Guests wait until you let them in.</p>}
    </div>
  );
}

export function Waiting({ share }) {
  if (!share?.canAdmit || !share.requests.length) return null;
  return (
    <div className="space-y-1.5 border-b border-white/10 p-3" data-testid="mm-guest-lobby">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-white">
        <span className="relative flex h-2 w-2" data-testid="mm-guest-lobby-nudge" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7FA8D6] opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7FA8D6]" />
        </span>
        Waiting to join ({share.requests.length})
      </p>
      {share.requests.map((r) => (
        <div key={r.request_id} className="flex items-center justify-between gap-2" data-testid={`mm-guest-req-${r.request_id}`}>
          <span className="truncate text-[13px] text-slate-200">{r.name}{r.email ? <span className="text-slate-400"> · {r.email}</span> : null}</span>
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" className={`${smBtn} bg-[#4575A8] text-white hover:bg-[#3A6491]`} disabled={share.deciding === r.request_id}
              onClick={() => share.decide(r.request_id, 'admit')} data-testid={`mm-guest-admit-${r.request_id}`}>Admit</button>
            <button type="button" className={`${smBtn} text-slate-400 hover:bg-white/10 hover:text-white`} disabled={share.deciding === r.request_id}
              onClick={() => share.decide(r.request_id, 'deny')} data-testid={`mm-guest-deny-${r.request_id}`}>Deny</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Person({ p, me, order, host, onLower }) {
  const name = p.user_name || 'Guest';
  const isHost = !!host?.hostId && p.user_id === host.hostId;
  return (
    <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-white/5" data-testid={`mm-person-${me ? 'local' : p.session_id}`}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#4575A8] text-xs font-semibold text-white">{name.slice(0, 1).toUpperCase()}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-white">{name}{me && <span className="text-slate-400"> (you)</span>}</p>
        {isHost && <p className="text-[10px] font-bold uppercase tracking-wider text-[#7FA8D6]" data-testid={`mm-host-badge-${me ? 'local' : p.session_id}`}>Host</p>}
      </div>
      {order && (
        <span className="mm-hand-pop flex items-center gap-0.5 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold text-black" data-testid={`mm-person-hand-${me ? 'local' : p.session_id}`}>
          <Hand className="h-3 w-3" />{order}
        </span>
      )}
      {order && (me || host?.canLower) && (
        <button type="button" className={`${smBtn} text-amber-300 hover:bg-amber-400/15`} onClick={() => onLower(p.session_id)} data-testid={`mm-lower-hand-${me ? 'local' : p.session_id}`}>
          Lower
        </button>
      )}
      {!me && host?.canMakeHost && isMember(p) && (
        <button type="button" className={`${smBtn} text-slate-300 hover:bg-white/10 hover:text-white`} onClick={() => host.makeHost(p.user_id)}
          title={`Make ${name} the host`} data-testid={`mm-make-host-${p.session_id}`}>
          <Crown className="h-3.5 w-3.5" /> Make host
        </button>
      )}
      {p.audio ? <Mic className="h-4 w-4 shrink-0 text-slate-300" /> : <MicOff className="h-4 w-4 shrink-0 text-red-400" />}
      {p.video ? <Video className="h-4 w-4 shrink-0 text-slate-300" /> : <VideoOff className="h-4 w-4 shrink-0 text-red-400" />}
    </div>
  );
}

function Participants({ dc, hands, share, host }) {
  const me = dc.local?.session_id;
  const canLower = !!host?.canLower;
  const people = Object.values(dc.participants).filter((p) => p?.session_id)
    .sort((a, b) => (hands.get(a.session_id) || 1e9) - (hands.get(b.session_id) || 1e9)
      || (b.session_id === me) - (a.session_id === me) || String(a.user_name).localeCompare(String(b.user_name)));
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="mm-participants">
      {share && <Invite share={share} />}
      <Waiting share={share} />
      <div className="flex items-center justify-between px-3 pb-1 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">In the meeting ({people.length})</p>
        {canLower && hands.size > 0 && (
          <button type="button" className={`${smBtn} text-amber-300 hover:bg-amber-400/15`} onClick={() => dc.lowerHand(null)} data-testid="mm-lower-all-hands">Lower all hands</button>
        )}
      </div>
      <div className="px-1.5 pb-3">
        {people.map((p) => (
          <Person key={p.session_id} p={p} me={p.session_id === me} order={hands.get(p.session_id)} host={host} onLower={dc.lowerHand} />
        ))}
      </div>
    </div>
  );
}

export default function SidePanel({ panel, setPanel, dc, hands, share, chat, overlay, host }) {
  const tab = (id, label) => (
    <button type="button" onClick={() => setPanel(id)} data-testid={`mm-panel-tab-${id}`}
      className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors duration-150 ${panel === id ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white'}`}>
      {label}
    </button>
  );
  return (
    <aside className={`flex min-h-0 flex-col border-l border-white/10 bg-[#0D1B2A] ${overlay ? 'absolute inset-y-0 right-0 z-30 w-[min(22rem,90%)] shadow-2xl' : 'w-80 shrink-0'}`}
      data-testid="mm-side-panel">
      <div className="flex items-center justify-between gap-2 border-b border-white/10 px-2 py-1.5">
        <div className="flex items-center gap-1">{tab('participants', `Participants (${Object.keys(dc.participants).length})`)}{tab('chat', 'Chat')}</div>
        <button type="button" onClick={() => setPanel(null)} aria-label="Close panel" className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" data-testid="mm-panel-close">
          <X className="h-4 w-4" />
        </button>
      </div>
      {panel === 'chat'
        ? <ChatPanel chat={chat} bare disabled={dc.phase !== 'joined'} className="min-h-0 flex-1" />
        : <Participants dc={dc} hands={hands} share={share} host={host} />}
    </aside>
  );
}
